import { useEffect, useId, useRef, useState } from 'react';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/query-client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertAction, AlertDescription } from '@/components/reui/alert';
import { Badge } from '@/components/reui/badge';
import { useCambiarEstadoRedes, useEstadoRedes } from '@/hooks/use-estado-redes';
import { ESTADOS_REDES_OPCIONES, type EstadoRedes } from '@/types/api';
import { AVISO_ESCRITURA_LISTA_CC_OFF, AVISO_ESCRITURA_LISTA_CC_OFF_DETALLE, escrituraListaCCHabilitada } from '@/lib/piloto';
import { faltantesPaso, unirFaltantes } from '@/components/rdt/paso-validacion';
// Solo tipos (se borran al compilar): la home no carga nada del Avivador.
import type { EstadoRedesFormProps } from '@/components/avivador/panel-avivador-tipos';
import { TarjetasRadio, type OpcionTarjeta } from '@/components/comunes/tarjetas-radio';
import { PlacaCc } from './placa-cc';
import { EstadoRedesBadge } from './estado-obra-badge';
import { HistorialEstadoRedes } from './historial-estado-redes';

// Mismo criterio de errores que el resto de la app (obra-detalle.tsx y
// demás): cada archivo con mutaciones redeclara su propia copia, no hay
// helper centralizado (ver relevamiento §5).
function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

// El título usa <PlacaCc> SIEMPRE neutra (sin color/colorTexto): el contexto
// de GET /api/obras/:cc/estado-redes (§4 del diseño) no trae color de
// cliente.

// ¿El texto se corta? Mismo criterio que card-mosaico.tsx y
// panel-avivador-cabecera.tsx (cada archivo redeclara su copia, no hay
// helper centralizado — ver relevamiento §5).
function estaCortado(el: HTMLElement | null): boolean {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

/** Chip del estado de redes actual (R0: `EstadoRedesBadge`), con el texto
 *  completo en Tooltip SOLO si se corta (se mide al abrir; nunca `title`).
 *  «RDT» ADENTRO de la píldora, como la rc.1 (V2, 28/9: decisión de Juan). */
function ChipEstadoRedes({ estado }: { estado: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <Tooltip
      onOpenChange={(abierto, detalles) => {
        if (abierto && !estaCortado(ref.current)) detalles.cancel();
      }}
    >
      <TooltipTrigger render={<span ref={ref} className="min-w-0 shrink truncate" />}>
        <EstadoRedesBadge estado={estado} prefijo="RDT" />
      </TooltipTrigger>
      <TooltipContent>{estado}</TooltipContent>
    </Tooltip>
  );
}

// Pluralización es-AR de «rede» — en RedeApp es sustantivo femenino (una
// rede, las redes; no «red»). 0 y 2+ llevan plural; 1 es singular con
// concordancia de género según la forma pedida. QA en vivo de Juan
// (21/9/2026): «1 redes abiertas» salía tal cual en la línea de contexto,
// el aviso ámbar y el toast — un solo lugar para no repetir el bug.
function pluralRedes(n: number, forma: 'abierta' | 'archivada'): string {
  if (n === 1) return forma === 'abierta' ? '1 rede abierta' : '1 archivada';
  return forma === 'abierta' ? `${n} redes abiertas` : `${n} archivadas`;
}

// Avivador v2 (design_avivador.md §12.2 punto 12, Encargo 3; hoy R4 del
// Avivador v2): el cuerpo puro se separa del `<Dialog>` para que
// `panel-avivador-accion.tsx` pueda montarlo directo (sin Dialog, sin scrim —
// el panel ya pinta su propio marco). A diferencia de `PasoForm`
// (paso-forms.tsx), acá SÍ conviene distinguir éxito de cancelación con dos
// callbacks separados en vez de un `onClose` ambiguo: el propio
// `handleConfirmar` YA sabe cuál de los dos pasó (su `mutation.onSuccess`).
// `EstadoRedesDialog`, más abajo, sigue exponiendo una sola señal
// (`onOpenChange`) — sin cambios para sus consumidores actuales (obra-card.tsx,
// header-obra.tsx) — y ahí sí sigue habiendo ambigüedad entre pasar
// `onDone`/`onCancel` al mismo `onOpenChange(false)`, pero eso ya no le
// importa a nadie porque el propio Dialog cierra igual en los dos casos.
//
// Props (contrato `EstadoRedesFormProps` de components/avivador/
// panel-avivador-tipos.ts): `onDone` = el estado se guardó de verdad
// (mutation.onSuccess; opcional desde T4a, las llamadas de siempre lo pasan
// igual); `onCancel` = el usuario canceló sin guardar nada;
// `onPendingChange` reporta `mutation.isPending` hacia arriba — lo usa
// `EstadoRedesDialog` para no dejar cerrar por backdrop/Escape con el POST en
// vuelo (mismo criterio que ya tenía antes de este split). El dorso no lo
// necesita: no tiene backdrop ni Escape propio en este nivel.
// T4a del Avivador v2 (§13 7.2 y 8.3), opcional: `variante="avivador"` (el
// panel lateral en R4) = sin toast de éxito (ni su Deshacer: la caja verde
// del panel confirma), «Falta: …» a la izquierda de los botones, primario
// «Definir estado»; `onRegistrado` con el estado y la nota enviados (en
// cualquier variante, antes de `onDone`). Con la escritura a Lista CC
// apagada (piloto) el form queda como hoy también en el panel: aviso,
// selector deshabilitado, sin nota ni «Falta:», «Cerrar».
//
// `sinHistorial` (R1, migración a ReUI — plan_migracion_reui.md, Ajuste 18):
// opcional, default false/undefined = pinta `HistorialEstadoRedes` dentro del
// bloque de contexto (home y header de la obra, como siempre). En `true`
// (el panel del Avivador, R4) el form NO lo pinta: el panel
// lo monta aparte, debajo del form, como su «Actividad», y no lo duplica.
// Está en `EstadoRedesFormProps` (panel-avivador-tipos.ts).

// Diálogo/dorso «Estado de redes» (design_estado_redes_obra.md §4): selector
// de los 5 estados de Estado.Redes con contexto (estado actual, redes
// abiertas/archivadas, historial de obra_eventos) + nota. Asume que `cc` ya
// resolvió un `contexto` real — el caller (EstadoRedesDialog o el dorso de
// la card) es quien maneja el loading/error de `useEstadoRedes(cc)` antes
// de montar esto, así que acá alcanza con un guard defensivo.
export function EstadoRedesForm({
  cc,
  onDone,
  onCancel,
  onPendingChange,
  variante = 'obra',
  onRegistrado,
  sinHistorial,
}: EstadoRedesFormProps) {
  const enPanel = variante === 'avivador';
  const query = useEstadoRedes(cc);
  const mutation = useCambiarEstadoRedes();
  // Mutación INDEPENDIENTE para «Deshacer» del toast (hallazgo del review
  // adversarial): si compartiera `mutation`, mientras el deshacer está en
  // vuelo el CTA del formulario ("Guardar"/"Pasar a...") se leería
  // deshabilitado y en "Guardando…" sin que el usuario haya tocado nada.
  // El `useRef` es la guarda contra el doble clic en la acción del toast:
  // sonner no deshabilita el botón de la action por sí solo, así que sin
  // esto un doble clic dispara dos mutaciones de deshacer en simultáneo.
  const deshacer = useCambiarEstadoRedes();
  const deshaciendoRef = useRef(false);
  // Override manual del usuario sobre el radiogroup — null = "todavía no
  // tocó nada", en cuyo caso se muestra `contexto.estadoActual` como
  // seleccionado (ver `seleccionadoActivo` más abajo). Evitar un efecto de
  // sincronización para esto: con un efecto, el primer render tras cargar
  // `contexto` pintaría el radiogroup sin nada tildado (el efecto corre
  // recién después del commit) — computar el valor efectivo en el render
  // mismo no tiene ese frame de parpadeo.
  const [seleccionUsuario, setSeleccionUsuario] = useState<EstadoRedes | null>(null);
  const [nota, setNota] = useState('');
  // id del aviso del piloto (aria-describedby del selector y los botones).
  const avisoPilotoId = useId();
  // id del «Falta: …» del panel del Avivador (aria-describedby del primario).
  const faltaId = useId();

  // Al abrir con otro cc (o cerrar) se resetea todo — un form abierto no
  // debe heredar la nota/selección de la obra anterior.
  useEffect(() => {
    setSeleccionUsuario(null);
    setNota('');
  }, [cc]);

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isPending]);

  const contexto = query.data;
  if (!contexto) return null;

  // Piloto 4.0.0 (runbook_produccion_piloto.md §2.2): sin LISTA_CC_ESCRITURA=on
  // el backend no escribe (su POST responde 422). Se avisa ANTES de intentar:
  // aviso arriba, selector deshabilitado (queda tildado el estado actual),
  // sin nota y «Guardar» deshabilitado. El contexto y el historial se ven
  // igual. Cubre la home, el panel del Avivador y el dorso de la card (los
  // tres montan este form).
  const escrituraHabilitada = escrituraListaCCHabilitada(contexto);
  const describedBy = escrituraHabilitada ? undefined : avisoPilotoId;

  const seleccionadoActivo: EstadoRedes | null = escrituraHabilitada
    ? (seleccionUsuario ?? ((contexto.estadoActual ?? null) as EstadoRedes | null))
    : ((contexto.estadoActual ?? null) as EstadoRedes | null);

  const notaTrim = nota.trim();
  const notaObligatoria = seleccionadoActivo === 'Archivado' || seleccionadoActivo === 'No Aplica';
  const sinCambio = seleccionadoActivo === contexto.estadoActual;
  const faltaNota = notaObligatoria && notaTrim.length === 0;
  const disabledSubmit = !escrituraHabilitada || sinCambio || faltaNota || mutation.isPending;
  // QA en vivo (21/9): con la opción actual seleccionada (sin cambio) no
  // corresponde mostrar el aviso ámbar ni la etiqueta «obligatoria» — recién
  // aplican cuando hay un cambio real pendiente de guardar. La validación
  // del CTA (`faltaNota`, arriba) sigue usando `notaObligatoria` solo: si el
  // usuario ya escribió la nota para Archivado/No Aplica y después vuelve a
  // seleccionar el estado actual, el botón se deshabilita igual por
  // `sinCambio`, no hace falta acoplar `hayCambio` ahí.
  const hayCambio = !sinCambio;
  const mostrarNotaObligatoria = hayCambio && notaObligatoria;
  // «Falta: …» del panel del Avivador (§13 7.2): «el estado» mientras no haya
  // un cambio elegido, «la nota» para No Aplica / Archivado. Sin escritura
  // (piloto) no se muestra: el aviso de arriba ya explica por qué no se puede.
  const faltan =
    enPanel && escrituraHabilitada
      ? faltantesPaso('estado-redes', { estado: sinCambio ? null : seleccionadoActivo, nota }, 'avivador')
      : [];
  const describedByPrimario = describedBy ?? (faltan.length > 0 ? faltaId : undefined);

  // Opciones del radiogroup de los 5 estados (TarjetasRadio, R0): el hint del
  // catálogo (ESTADOS_REDES_OPCIONES) como descripción y el badge «actual»
  // solo en la opción que coincide con `contexto.estadoActual`. `size="sm"`
  // (10 px, no el `default` de R1/Ajuste 15) desde O6 de la QA del 28/9: la
  // rc.1 la tenía chica (mismo tamaño que este `sm`) y con `compacto` en
  // TarjetasRadio la tarjeta ya no tiene el aire de R1 para una etiqueta
  // grande.
  const opcionesEstado: readonly OpcionTarjeta<EstadoRedes>[] = ESTADOS_REDES_OPCIONES.map(opt => ({
    valor: opt.value,
    titulo: opt.value,
    descripcion: opt.hint,
    adorno:
      contexto.estadoActual === opt.value ? (
        <Badge variant="secondary" size="sm" radius="full">
          actual
        </Badge>
      ) : undefined,
  }));

  const handleConfirmar = (): void => {
    if (disabledSubmit || seleccionadoActivo === null) return;
    const estadoAnterior = contexto.estadoActual;
    const redesAbiertas = contexto.redes.abiertas;
    const nuevoEstado = seleccionadoActivo;
    const notaEnviada = notaTrim.length > 0 ? notaTrim : null;

    mutation.mutate(
      { cc: contexto.cc, estado: nuevoEstado, nota: notaEnviada },
      {
        onSuccess: () => {
          // Panel del Avivador (§13 8.3): sin toast — la caja verde del panel
          // es la confirmación, y ahí no hay Deshacer (§13 8.2).
          if (enPanel) {
            onRegistrado?.({ tipo: 'estado-redes', estado: nuevoEstado, nota: notaEnviada });
            onDone?.();
            return;
          }
          const description =
            (nuevoEstado === 'Archivado' || nuevoEstado === 'No Aplica') && redesAbiertas > 0
              ? redesAbiertas === 1
                ? 'Su rede abierta no cambia. Con el switch apagado deja de verse.'
                : `Sus ${pluralRedes(redesAbiertas, 'abierta')} no cambian. Con el switch apagado deja de verse.`
              : undefined;
          toast.success(`La obra ${contexto.cc} pasó a ${nuevoEstado}`, {
            description,
            action:
              estadoAnterior !== null
                ? {
                    label: 'Deshacer',
                    onClick: () => {
                      // Guarda contra el doble clic: sonner no deshabilita el
                      // botón de la action, así que sin el ref dos clics
                      // rápidos disparan dos mutaciones de deshacer.
                      if (deshaciendoRef.current) return;
                      deshaciendoRef.current = true;
                      deshacer.mutate(
                        { cc: contexto.cc, estado: estadoAnterior as EstadoRedes, nota: `Deshacer: vuelve a ${estadoAnterior}` },
                        {
                          onError: err => toast.error(errorMessage(err, 'No se pudo deshacer el cambio')),
                          onSettled: () => {
                            deshaciendoRef.current = false;
                          },
                        },
                      );
                    },
                  }
                : undefined,
          });
          onRegistrado?.({ tipo: 'estado-redes', estado: nuevoEstado, nota: notaEnviada });
          onDone?.();
        },
        // No se avisa nada más allá del toast de error — el form NO se
        // cierra si falla, el usuario puede corregir y reintentar.
        onError: err => toast.error(errorMessage(err, 'No se pudo cambiar el estado de redes')),
      },
    );
  };

  return (
    <>
      {/* Aviso del piloto (solo con la escritura a Lista CC apagada): `role`
          "note" (estático, no dispara por una acción) con los dos textos en
          AlertDescription y el MISMO id que referencian el selector y los
          botones (aria-describedby). */}
      {!escrituraHabilitada && (
        <Alert id={avisoPilotoId} role="note">
          <Info aria-hidden="true" />
          <AlertDescription>{AVISO_ESCRITURA_LISTA_CC_OFF}</AlertDescription>
          <AlertDescription>{AVISO_ESCRITURA_LISTA_CC_OFF_DETALLE}</AlertDescription>
        </Alert>
      )}

      {/* Bloque de contexto: estado actual + conteo de redes + historial */}
      <div className="rounded-[9px] border border-border-subtle bg-surface-2 p-3.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-muted-foreground">
          <span className="text-text-tertiary">Estado actual</span>
          {contexto.estadoActual ? <ChipEstadoRedes estado={contexto.estadoActual} /> : <span className="text-text-tertiary">Sin estado</span>}
          {/* El «·» va PEGADO al texto que sigue, en el mismo item del flex:
              separado (como antes) podía quedar colgado solo al final de una
              línea cuando el renglón envuelve (Ajuste 6). */}
          <span className="min-w-0">
            <span aria-hidden="true" className="text-border-strong">
              ·
            </span>{' '}
            {pluralRedes(contexto.redes.abiertas, 'abierta')} · {pluralRedes(contexto.redes.archivadas, 'archivada')}
          </span>
        </div>

        {!sinHistorial && (
          <div className="mt-3 border-t border-border-subtle pt-3">
            <HistorialEstadoRedes historial={contexto.historial} />
          </div>
        )}
      </div>

      {/* Radiogroup de los 5 estados (TarjetasRadio, R0, sobre c-radio-group-7).
          `compacto` (O6 de la QA del 28/9): paridad con el radiogroup a mano
          de la rc.1 — acá era el 70 % de los 144 px de más que medía este
          diálogo. */}
      <TarjetasRadio
        valor={seleccionadoActivo}
        onValorChange={setSeleccionUsuario}
        opciones={opcionesEstado}
        columnas={1}
        disabled={!escrituraHabilitada}
        compacto
        aria-label="Nuevo estado de redes"
        aria-describedby={describedBy}
      />

      {mostrarNotaObligatoria && contexto.redes.abiertas > 0 && (
        <Alert variant="warning" role="status">
          <TriangleAlert aria-hidden="true" />
          <AlertDescription>
            Esta obra tiene {pluralRedes(contexto.redes.abiertas, 'abierta')}. La obra deja de verse en la home, pero{' '}
            {contexto.redes.abiertas === 1 ? 'su rede no se archiva ni se toca.' : 'sus redes no se archivan ni se tocan.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Sin escritura (piloto) no hay nota que cargar: el campo no se muestra.
          O6 de la QA del 28/9 (paridad con la rc.1): el estado de la nota
          («opcional» / «obligatoria para X», como la rc.1) va en la MISMA línea que la
          etiqueta, no como FieldDescription en línea propia — ahí estaban
          42 de los 144 px de más que medía este diálogo. El <label> real
          (FieldLabel htmlFor) sigue siendo el único asociado al textarea;
          el <em> solo agrega texto a su nombre accesible, no rompe nada. */}
      {escrituraHabilitada && (
        <Field>
          <FieldLabel htmlFor="estado-redes-nota">
            Nota
            {mostrarNotaObligatoria ? (
              <em className="font-normal text-accent-strong">obligatoria para {seleccionadoActivo}</em>
            ) : (
              <em className="font-normal text-text-tertiary">opcional</em>
            )}
          </FieldLabel>
          <Textarea
            id="estado-redes-nota"
            value={nota}
            onChange={e => setNota(e.target.value)}
            maxLength={500}
            placeholder={
              !mostrarNotaObligatoria
                ? enPanel
                  ? 'Por qué se define este estado'
                  : 'Motivo del cambio'
                : seleccionadoActivo === 'Archivado'
                  ? 'Por qué se archiva la obra'
                  : 'Por qué se marca como No Aplica'
            }
          />
        </Field>
      )}

      {/* Card v3 del Avivador (§12.2 punto 2): footer `sticky bottom-0` para
          que "Guardar"/"Pasar a X" nunca quede fuera de vista cuando este
          form corre dentro del scroll propio del dorso (~700px de contenido
          contra 264-428px disponibles). Dentro del <Dialog> de siempre esto
          no cambia nada visible: el footer ya estaba al final del contenido
          y sigue estándolo, solo que ahora se ancla si el contenido scrollea. */}
      <div data-slot="estado-redes-pie" className="sticky bottom-0 flex flex-col gap-3 border-t border-border-subtle bg-surface-card pt-4">
        {escrituraHabilitada && (
          <p className="m-0 text-[11px] text-text-tertiary">La nota queda en RedeApp (historial de la obra y auditoría), no en SharePoint.</p>
        )}
        {enPanel ? (
          // Panel del Avivador (artifact `.f-acts`): «Falta: …» a la
          // izquierda, «Cancelar» / «Cerrar» y «Definir estado» — Button
          // default/outline sin clases propias (44 px en táctil por el cva).
          <div className="flex flex-wrap items-center justify-end gap-2">
            {faltan.length > 0 && (
              <p id={faltaId} className="m-0 mr-auto min-w-0 text-[11.5px] text-muted-foreground">
                Falta: {unirFaltantes(faltan)}
              </p>
            )}
            <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending} aria-describedby={describedBy}>
              {escrituraHabilitada ? 'Cancelar' : 'Cerrar'}
            </Button>
            <Button type="button" onClick={handleConfirmar} disabled={disabledSubmit} focusableWhenDisabled aria-describedby={describedByPrimario}>
              {mutation.isPending ? 'Guardando…' : 'Definir estado'}
            </Button>
          </div>
        ) : (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending} aria-describedby={describedBy}>
              {escrituraHabilitada ? 'Cancelar' : 'Cerrar'}
            </Button>
            <Button type="button" onClick={handleConfirmar} disabled={disabledSubmit} aria-describedby={describedBy}>
              {mutation.isPending ? 'Guardando…' : sinCambio ? 'Guardar' : `Pasar a ${seleccionadoActivo}`}
            </Button>
          </DialogFooter>
        )}
      </div>
    </>
  );
}

interface DialogProps {
  cc: string | null;
  onOpenChange: (open: boolean) => void;
}

// Envoltura fina para los consumidores de siempre (obra-card.tsx,
// header-obra.tsx, sin cambios): monta `EstadoRedesForm` dentro de un
// `<Dialog>` real, con el título/descripción y el loading/error que antes
// vivían acá mismo (estado-redes-dialog.tsx:182-195 antes de este split).
export function EstadoRedesDialog({ cc, onOpenChange }: DialogProps) {
  const query = useEstadoRedes(cc);
  const [pending, setPending] = useState(false);

  return (
    <Dialog
      open={cc !== null}
      onOpenChange={open => {
        // Mismo criterio que edicion-rdt.tsx: con el POST en vuelo no se
        // cierra por overlay/Escape — solo queda la vía explícita del botón
        // Cancelar (deshabilitado más abajo mientras `isPending`).
        if (!open && pending) return;
        onOpenChange(open);
      }}
    >
      {/* pb-0 + el padding de abajo en el pie (QA automática de la rc.6): con el
          p-6 del scroller, el pie sticky se anclaba 24 px arriba del borde y el
          form se veía pasando por debajo de Cancelar/Guardar al scrollear (ya
          pasaba en la rc.1). Mismo patrón que el panel del Avivador. */}
      <DialogContent className="sm:max-w-[520px] max-h-[88vh] overflow-y-auto pb-0 [&_[data-slot=estado-redes-pie]]:pb-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <PlacaCc cc={cc ?? ''} />
            Estado de redes
          </DialogTitle>
          <DialogDescription>
            {/* Piloto 4.0.0: con la escritura apagada no se promete escribir
                ni auditar — el aviso del form explica dónde se cambia. */}
            {query.data
              ? `${query.data.denominacion}${query.data.cliente ? ` · ${query.data.cliente}` : ''}. ${
                  escrituraListaCCHabilitada(query.data)
                    ? 'El estado vive en Lista CC (SharePoint): el cambio se escribe ahí y queda auditado con tu nombre.'
                    : 'El estado vive en Lista CC (SharePoint).'
                }`
              : 'El estado vive en Lista CC (SharePoint): el cambio se escribe ahí y queda auditado con tu nombre.'}
          </DialogDescription>
        </DialogHeader>

        {query.isPending ? (
          <div className="flex flex-col gap-3" aria-hidden="true">
            <Skeleton className="h-[64px] rounded-[9px] bg-surface-2" />
            <Skeleton className="h-[220px] rounded-[9px] bg-surface-2" />
            <Skeleton className="h-16 rounded-[9px] bg-surface-2" />
          </div>
        ) : query.isError ? (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertDescription>No se pudo cargar el estado de redes de la obra.</AlertDescription>
            <AlertAction>
              <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
                Reintentar
              </Button>
            </AlertAction>
          </Alert>
        ) : (
          <EstadoRedesForm cc={cc} onDone={() => onOpenChange(false)} onCancel={() => onOpenChange(false)} onPendingChange={setPending} />
        )}
      </DialogContent>
    </Dialog>
  );
}
